import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { SubPage, Tab } from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { SiteAuditComponent } from '@client/pages/engineering/components';
import { HttpService } from '@core/services';
import { SiteAuditHttpService } from '@core/services/http';
import { getProjectBundleIDParamFromUrl } from '@core/utils/methods';
import { EngineeringActions } from '@store/actions';
import { EngineeringSelectors, UserSelectors } from '@store/selectors';

import './styles.scss';

type EngineeringDetailsSiteAuditTabProps = RouteComponentProps & {
  isActive?: boolean;
};

export const EngineeringDetailsSiteAuditTab: FC<
  EngineeringDetailsSiteAuditTabProps
> = ({ isActive }) => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();
  const isLoading = useSelector(
    EngineeringSelectors.getTabLoadingState('siteAudit'),
  );
  const isDataLoaded = useSelector(
    EngineeringSelectors.getTabLoadedState('siteAudit'),
  );
  const permissions = useSelector(
    UserSelectors.getEngineeringSiteAuditPermissions,
  );
  const canEdit = useSelector(
    UserSelectors.canEdit(
      'engineering',
      SubPage.EngineeringBrowse,
      Tab.SiteAudit,
    ),
  );

  useEffect(() => {
    if (isActive && !isDataLoaded && !isLoading) {
      dispatch(
        EngineeringActions.setTabLoading.done({
          tabName: 'siteAudit',
          isLoading: true,
        }),
      );

      const loadSiteAuditTab = async (): Promise<void> => {
        try {
          await HttpService.getHttpRequests(SiteAuditHttpService).getSiteAudit(
            projectBundleID,
          );
          dispatch(
            EngineeringActions.setTabLoaded.done({
              tabName: 'siteAudit',
              isLoaded: true,
            }),
          );
        } catch {
          dispatch(
            EngineeringActions.setTabError.done({
              tabName: 'siteAudit',
              hasError: true,
            }),
          );
        }
      };

      loadSiteAuditTab();
    }
  }, [dispatch, isActive, isDataLoaded, isLoading, projectBundleID]);

  if (isLoading || !isDataLoaded) {
    return (
      <div className="prov-engineering-site-audit-tab">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="prov-engineering-site-audit-tab">
      <SiteAuditComponent
        canUserEdit={canEdit}
        defaultEditing={false}
        permissions={permissions}
      />
    </div>
  );
};
